import { useEffect, useMemo } from 'react';
import { useMappingProjectReadiness } from './useMappingProjectReadiness';
import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { traceMappingFlow } from '../lib/mappingTrace';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';

export interface UseMappingReadinessNotificationsArgs {
  mappingReadiness: ReturnType<typeof useMappingProjectReadiness>;
  activeDocumentInfo: ReturnType<typeof useActiveDocumentInfoContext>;
  activeProjectSessionKey: string | null;
  resolvedProfileKey: string;
  focusRefs: MappingWorkspaceFocusRefs;
  showMappingInfo: (message: string, duration?: number) => void;
}

export function useMappingReadinessNotifications({
  mappingReadiness,
  activeDocumentInfo,
  activeProjectSessionKey,
  resolvedProfileKey,
  focusRefs,
  showMappingInfo,
}: UseMappingReadinessNotificationsArgs): void {
  const readinessRepairableParametersKey = mappingReadiness.repairableParameterNames.join('|');
  const mappingRepairToastKey = useMemo(() => {
    if (mappingReadiness.status !== 'repairing' || !mappingReadiness.message) {
      return null;
    }

    return [
      activeProjectSessionKey || 'no-project',
      resolvedProfileKey || 'no-profile',
      mappingReadiness.message,
      readinessRepairableParametersKey,
    ].join('::');
  }, [
    activeProjectSessionKey,
    mappingReadiness.message,
    mappingReadiness.status,
    readinessRepairableParametersKey,
    resolvedProfileKey,
  ]);

  useEffect(() => {
    if (!mappingRepairToastKey || !mappingReadiness.message) {
      return;
    }

    if (focusRefs.shownMappingRepairToastKeys.current.has(mappingRepairToastKey)) {
      traceMappingFlow('mapping readiness repair toast skipped', {
        documentName: activeDocumentInfo.documentName,
        projectKey: activeDocumentInfo.projectKey,
        modelVersion: activeDocumentInfo.modelVersion,
        profileKey: resolvedProfileKey,
        reason: 'alreadyShown',
        repairableCount: mappingReadiness.repairableParameterNames.length,
      });
      return;
    }

    focusRefs.shownMappingRepairToastKeys.current.add(mappingRepairToastKey);
    showMappingInfo(mappingReadiness.message, 3000);
    traceMappingFlow('mapping readiness repair toast shown', {
      documentName: activeDocumentInfo.documentName,
      projectKey: activeDocumentInfo.projectKey,
      modelVersion: activeDocumentInfo.modelVersion,
      profileKey: resolvedProfileKey,
      repairableCount: mappingReadiness.repairableParameterNames.length,
    });
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.modelVersion,
    activeDocumentInfo.projectKey,
    mappingReadiness.message,
    mappingReadiness.repairableParameterNames.length,
    mappingRepairToastKey,
    resolvedProfileKey,
    showMappingInfo,
  ]);
}
